import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { RefreshCw, Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const when = (iso) => (iso ? new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : '—');

export const UmrahRefreshPanel = () => {
  const [run, setRun] = useState(null);
  const [schedule, setSchedule] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const load = async () => {
    try { const { data } = await axios.get(`${API_URL}/api/admin/umrah/refresh/last`, { withCredentials: true }); setRun(data.run); setSchedule(data.schedule); }
    catch (e) { setErr(e.response?.data?.detail || 'Failed to load refresh status'); }
  };
  useEffect(() => { load(); }, []);
  const runNow = async () => {
    setBusy(true); setErr(null);
    try { const { data } = await axios.post(`${API_URL}/api/admin/umrah/refresh/run`, {}, { withCredentials: true }); setRun(data.run); }
    catch (e) { setErr(e.response?.data?.detail || 'Refresh failed'); }
    finally { setBusy(false); }
  };
  const ok = run?.status === 'OK';
  return (
    <div className="rounded-xl border border-slate-200 p-4 space-y-3" data-testid="umrah-refresh-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="font-semibold text-slate-900 flex items-center gap-2"><RefreshCw className="h-4 w-4 text-[#FF6600]" />Nightly hotel & FX refresh</div>
          <div className="text-xs text-slate-500">Scheduled {schedule || 'daily'} · refreshes the SAR→GBP rate, checks every partner hotel has rates for the next 12 months and warms package pricing.</div>
        </div>
        <Button variant="outline" size="sm" onClick={runNow} disabled={busy} data-testid="umrah-refresh-run-btn">{busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}Run now</Button>
      </div>
      {err && <p className="text-sm text-red-600" data-testid="umrah-refresh-error">{err}</p>}
      {!run ? <p className="text-sm text-slate-500" data-testid="umrah-refresh-none">No refresh has run yet.</p> : (
        <div className="space-y-2 text-sm" data-testid="umrah-refresh-last">
          <div className="flex flex-wrap items-center gap-2">
            <Badge className={ok ? 'bg-emerald-100 text-emerald-800' : run.status === 'FAILED' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'} data-testid="umrah-refresh-status">{run.status}</Badge>
            <span className="text-slate-700">Last run {when(run.finished_at || run.started_at)} ({run.trigger}) · {run.duration_s ?? '—'}s</span>
            {run.fx && <span className="text-slate-700">· SAR/GBP {run.fx.sar_per_gbp ? Number(run.fx.sar_per_gbp).toFixed(3) : 'not refreshed'}</span>}
          </div>
          {run.alerts?.length ? (
            <ul className="space-y-1" data-testid="umrah-refresh-alerts">{run.alerts.map((a, i) => <li key={i} className="flex items-start gap-2 text-amber-800"><AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />{a}</li>)}</ul>
          ) : run.status !== 'FAILED' && <div className="flex items-center gap-2 text-emerald-700"><CheckCircle2 className="h-4 w-4" />All partner hotels have rates for the next 12 months.</div>}
          {run.error && <p className="text-red-600">{run.error}</p>}
        </div>
      )}
    </div>
  );
};
